import { useSearchParams } from "react-router-dom";
import React from "react";
import { useQuery } from '@tanstack/react-query';

import { findObjectByKeyValue, numToPrice } from "../components/utils.js";
import { RangeSlider } from "../components/transfers/RangeSlider";
import { TeamDropdown } from "../components/common/Dropdown.jsx";
import { PlayersBlock } from "../components/transfers/PlayersBlock";
import { TransfersPageNav } from "../components/transfers/TransfersPageNav";
import { TransferFlowPopup } from "../components/transfers/Popups";
import { useAuth } from "../hooks/useAuth";
import { useActiveLeague } from "../hooks/useActiveLeague";
import TransferService from "../services/transferService";
import CountdownTimer from "../components/common/CountdownTimer";
import { isActiveTransferBid } from "../utils/transferBidStatus.js";



function TransfersOngoingPage() {
  const { user } = useAuth();
  const { activeLeague } = useActiveLeague();
  // Fallback leagueId from URL param if context unresolved
  const [searchParams] = useSearchParams();
  const leagueIdParam = searchParams.get('leagueId');
  const derivedLeagueId = activeLeague?._id || leagueIdParam || null;
  const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';

  // Team info state – fetched from backend so sidebar stays consistent with other pages
  const [teamInfo, setTeamInfo] = React.useState({
    boosted: false,
    startingBudget: 0,
    transferBudget: 0,
    remainingBudget: 0,
    squadSize: 0,
    id: null,
    name: ''
  });

  React.useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!user) return;
      try {
        const leagueId = derivedLeagueId;
        const response = await TransferService.getTeamInfo(leagueId);
        setTeamInfo({
          boosted: response.data.boosted || false,
          startingBudget: response.data.startingBudget || 0,
          transferBudget: response.data.transferBudget || 0,
          remainingBudget: response.data.remainingBudget || 0,
          squadSize: response.data.squadSize || 0,
          id: response.data.teamId,
          name: response.data.name || ''
        });
      } catch (err) {
        console.error('Error fetching team info:', err);
      }
    };

    if (derivedLeagueId) {
      fetchTeamInfo();
    }
  }, [user, derivedLeagueId]);

  const showFields = {
    position: "Pos",
    name: "Name",
    value: "Value",
    myOffer: "My offer",
    status: "Status",
    timeRemaining: "Time",
  };

  function filterObjects(objects, filters) {
    return objects.filter((obj) => {
      const matchesName = filters.name
        ? obj.name.toLowerCase().includes(filters.name.toLowerCase())
        : true;
      const matchesPosition = filters.position
        ? obj.position === filters.position
        : true;
      const matchesMinVal =
        filters.minVal !== null ? obj.value >= filters.minVal : true;
      const matchesMaxVal =
        filters.maxVal !== null ? obj.value <= filters.maxVal : true;

      return (
        matchesName &&
        matchesPosition &&
        matchesMinVal &&
        matchesMaxVal
      );
    });
  }

  function findMinMax(arr, field) {
    if (arr.length === 0) {
      return null; // Возвращаем null, если массив пустой
    }

    let min = arr[0][field];
    let max = arr[0][field];

    for (const obj of arr) {
      if (obj[field] < min) {
        min = obj[field];
      }
      if (obj[field] > max) {
        max = obj[field];
      }
    }

    return {
      min: min,
      max: max,
    };
  }

  // TanStack Query for bids data
  const { data: bids, isLoading: loading, error, refetch } = useQuery({
    queryKey: ['userBids', derivedLeagueId],
    queryFn: async () => {
      const response = await TransferService.getBids(derivedLeagueId);
      return response || { incoming: [], outgoing: [] };
    },
    enabled: !!derivedLeagueId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // State for popup and selected player (align with TransfersPage)
  const [selectedPlayer, setSelectedPlayer] = React.useState(null);
  const [popOpen, setPopOpen] = React.useState(false);
  const [popEvent, setPopEvent] = React.useState(null);

  const uniqueTitles = new Set();
  const activeOutgoingBids = (bids?.outgoing || []).filter(isActiveTransferBid);
  const playersInfo = activeOutgoingBids;
  
  // Convert bids to players format for position processing
  const playersForPositions = playersInfo
    .map(bid => bid.player)
    .filter(player => player && player.position);
    
  const unicPositions = playersForPositions.reduce((acc, current) => {
    if (!uniqueTitles.has(current.position)) {
      uniqueTitles.add(current.position);
      acc.push({ id: acc.length + 1, title: current.position });
    }
    return acc;
  }, []);

  unicPositions.unshift({ id: 0, title: "All" });

  // Convert bids to players format for calculations
  const playersForCalculation = playersInfo
    .map(bid => ({
      value: bid.player?.value
    }))
    .filter(player => player.value != null);

  const values = findMinMax(playersForCalculation, "value");

  const initFilters = {
    name: null,
    position: null,
    minVal: values?.min || 0,
    maxVal: values?.max || 200000000,
  };

  const [filters, setFilters] = React.useState(initFilters);

  // Update filters when data changes
  React.useEffect(() => {
    if (!loading && playersForCalculation.length > 0) {
      const newValues = findMinMax(playersForCalculation, "value");
      
      setFilters(prevFilters => ({
        ...prevFilters,
        minVal: newValues?.min || 0,
        maxVal: newValues?.max || 200000000,
      }));
    }
  }, [loading, playersForCalculation.length]);


  function setName(name) {
    if (filters.name !== name) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        name: name,
      }));
    }
  }

  function setPosition(id) {
    const positionObj = findObjectByKeyValue(unicPositions, "id", id);
    const position =
      positionObj && positionObj.title !== "All" ? positionObj.title : null;

    if (filters.position !== position) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        position: position,
      }));
    }
  }

  function setMinMaxValue(min, max) {
    if (filters.minVal !== min || filters.maxVal !== max) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        minVal: min,
        maxVal: max,
      }));
    }
  }
  const dropRef = React.useRef();
  const rangeValRef = React.useRef();
  
  // Convert bids to players format for the PlayersBlock component
  const players = activeOutgoingBids.map(bid => {
    // Use the bidStatus from backend to determine status
    const status = bid.bidStatus || 'pending';
    const currentTeam = bid.player?.currentTeam
      ? {
          ...bid.player.currentTeam,
          id: bid.player.currentTeam.id ?? bid.player.currentTeam._id,
          _id: bid.player.currentTeam._id ?? bid.player.currentTeam.id,
        }
      : null;

    return {
      id: bid._id,
      wyId: bid.player?.wyId,
      name: bid.player?.name || 'Unknown Player',
      position: bid.player?.position || 'N/A',
      value: bid.player?.value || 0,
      myOffer: bid.amount,
      isFreeAgent: bid.player?.isFreeAgent === true,
      currentTeam,
      currentPrice: bid.player?.currentPrice ?? null,
      status: status,
      timeRemaining: bid.expiresAt, // Pass the expiresAt date directly
      
      // Additional data for filtering
      age: bid.player?.age || 0,
      nationality: bid.player?.nationality || 'Unknown'
    };
  });

  // Apply filters to the players
  const filteredPlayers = filterObjects(players, filters);

  function clearFilters() {
    const currentValues = findMinMax(playersForCalculation, "value");
    
    const resetFilters = {
      name: null,
      position: null,
      minVal: currentValues?.min || 0,
      maxVal: currentValues?.max || 200000000,
    };
    
    setFilters(resetFilters);
    dropRef.current?.clear();
    rangeValRef.current?.clear();
  }

  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [teamOpen, setTeamOpen] = React.useState(false);

  const closePopup = () => {
    setPopOpen(false);
  };

  const handlePopEvent = (event) => {
    setPopEvent(event);
  };

  const selectPlayerForBid = (player) => {
    setSelectedPlayer(player);
  }

  const handlePopup = (event, player) => {
    setPopOpen(true);
    setPopEvent(event);
    if (player) setSelectedPlayer(player);
  };

  const handleBoost = (option) => {
    setPopOpen(false);
    console.log(option);
  };

  const handleSubmit = () => {
    setPopOpen(false);
    console.log("submited");
  };

  return (
    <div className="page-market">
      <div className="wrapper">
        <h1>TRANSFER MARKET</h1>

        <TransfersPageNav />

        <div className="market-grid">
          <div className="filters-col">
            <div className={`filters ${filtersOpen ? "active" : ""}`}>
              <button
                className="show-filters"
                onClick={() => {
                  setFiltersOpen(true);
                }}
              >
                SHOW FILTERS
              </button>

              <div className="form-block">
                <div className="filters-container">
                  <div className="form-group search-group">
                    <label htmlFor="i1">SEARCH BY NAME *</label>
                    <input
                      id="i1"
                      type="text"
                      value={filters.name === null ? "" : filters.name}
                      onChange={(event) => {
                        setName(event.target.value);
                      }}
                    />
                    <span className="ico icon-search" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="s1">POSITION</label>

                    <TeamDropdown
                      ref={dropRef}
                      props={unicPositions}
                      setId={(id) => setPosition(id)}
                      startVal={"POSITION"}
                    />
                  </div>

                  <div className="range-sliders">
                    <div className="form-group range-block">
                      <h4>VALUE</h4>

                      <RangeSlider
                        ref={rangeValRef}
                        min={values?.min}
                        max={values?.max}
                        sendMinMax={setMinMaxValue}
                      />
                    </div>
                  </div>
                </div>

                <div className="filters-bottom">
                  <button
                    className="hide-filters"
                    onClick={() => {
                      setFiltersOpen(false);
                    }}
                  >
                    HIDE FILTERS
                  </button>

                  <div className="reset-col">
                    <button
                      className="reset-filters"
                      onClick={() => {
                        clearFilters();
                      }}
                    >
                      Clear <span className="desc-text">filters</span>{" "}
                      <span className="ico icon-s-cross" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="list-col">
            {loading ? (
              <div className="loading-state">
                <p>Loading your active bids...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>Error: {error?.message || error}</p>
                <button onClick={() => refetch()}>Retry</button>
              </div>
            ) : filteredPlayers.length === 0 ? (
              <div className="empty-state">
                <p>You have no active transfer bids.</p>
              </div>
            ) : (
              <PlayersBlock 
                players={filteredPlayers} 
                showFields={showFields} 
                handlePopup={handlePopup}
                setSelectedPlayer={selectPlayerForBid}
              />
            )}
          </div>

          <div className={`team-col`}>
            <div className={`team-container ${teamOpen ? 'active' : ''}`}  >
              <button className="show-team"
                      onClick={() => {setTeamOpen(true)}}
              >
                SHOW TEAM
              </button>
              <button className="hide-team"
                      onClick={() => {setTeamOpen(false)}}
              >
                HIDE TEAM
              </button>
              <div className="info-row">
                <h4>My team</h4>

                <div className="info-container">
                  <div className="info-block">
                    <div className="i-title boost-title">
                      Transfer budget
                      {/* BOOST button temporarily hidden - will be needed in future */}
                      {/* <button
                        type="button"
                        className={`submit-btn trans-btn ${
                          teamInfo.boosted ? "disabled" : ""
                        }`}
                        onClick={() => {
                          handlePopup("boost");
                        }}
                      >
                        BOOST
                      </button> */}
                    </div>
                    <div className="i-value">
                      <span className="ico icon-nova" />
                      {numToPrice(
                        Number.isFinite(teamInfo.startingBudget) && teamInfo.startingBudget > 0
                          ? teamInfo.startingBudget
                          : teamInfo.transferBudget
                      )}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="i-title">Remaining budget</div>
                    <div className="i-value">
                      <span className="ico icon-nova" />
                      {numToPrice(teamInfo.remainingBudget)}
                    </div>
                    <div className="i-note">Drop or sell players to get transfer money back.</div>
                  </div>
                  <div className="info-block">
                    <div className="i-title">Squad size</div>
                    <div className="i-value">{teamInfo.squadSize} / 22</div>
                  </div>
                </div>

                <div
                  className={`tip ${
                    teamInfo.squadSize >= 22 ? "tip-success" : "tip-alert"
                  }`}
                >
                  Minimum squad size: 22 <span className="ico icon-r-check" />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <TransferFlowPopup
        info={teamInfo}
        popEvent={popEvent}
        setPopEvent={handlePopEvent}
        popOpen={popOpen}
        closePopup={closePopup}
        handleSubmit={handleSubmit}
        handleBoost={handleBoost}
        selectedPlayer={selectedPlayer}
        isQuickplayLeague={isQuickplayLeague}
      />
    </div>
  );
}

export { TransfersOngoingPage };
